import { BinEvent, GPSUpdatedEvent, RequestEvent, VideoRequestEvent, CommercialGpsUpdatedEvent } from "./events";
import * as signalR from "@microsoft/signalr";
import userStore from "@/store/userStore";
import appConfig from "@/plumbing/ApplicationSettings";
import { reaction, observable } from "mobx";
import SubscribableEvent from "subscribableevent";

const reconnetPolicy = {
  nextRetryDelayInMilliseconds: (retryContext: signalR.RetryContext) => {
    const delay = [0, 2000, 10000, 30000];
    if (retryContext.previousRetryCount < delay.length) {
      return delay[retryContext.previousRetryCount];
    }
    return delay[delay.length - 1];
  }
} as signalR.IRetryPolicy;

export class EventsHub {
  @observable connection?: signalR.HubConnection;
  group?: string;
  dispose?: () => void;
  startPromise?: Promise<void>;
  gpsUpdatedEvent = new SubscribableEvent<(payload: GPSUpdatedEvent) => void>();
  binEvent = new SubscribableEvent<(payload: BinEvent) => void>();
  requestEvent = new SubscribableEvent<(payload: RequestEvent) => void>();
  videoRequestEvent = new SubscribableEvent<(payload: VideoRequestEvent) => void>();
  commercialGpsUpdatedEvent = new SubscribableEvent<(payload: CommercialGpsUpdatedEvent) => void>();

  constructor() {
    setTimeout(() => {
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(`${appConfig.apiBaseUrl}/truck-events`, {
          accessTokenFactory: async () => {
            const user = await userStore.userPromise;

            if (!user) {
              console.warn("User is not set - will not be able to authenticate with signalR hub");
              return "";
            }

            return user.access_token;
          }
        })
        .withAutomaticReconnect(reconnetPolicy)
        .configureLogging(process.env.NODE_ENV === "production" ? signalR.LogLevel.Error : signalR.LogLevel.Debug)
        .build();

      this.connection.on("GpsPositionUpdated", (message: GPSUpdatedEvent) => {
        console.debug(`Received GPS Position Updated: ${JSON.stringify(message)}`);
        this.gpsUpdatedEvent.fire(message);
      });

      this.connection.on("CommercialGpsPositionUpdated", (message: CommercialGpsUpdatedEvent) => {
        console.debug(`Received Commercial GPS Position Updated: ${JSON.stringify(message)}`);
        this.commercialGpsUpdatedEvent.fire(message);
      });

      this.connection.on("BinEvent", (message: BinEvent) => {
        console.debug(`Received Bin Event: ${JSON.stringify(message)}`);
        this.binEvent.fire(message);
      });

      this.connection.on("RequestEvent", (message: RequestEvent) => {
        console.debug(`Received Request Event: ${JSON.stringify(message)}`);
        this.requestEvent.fire(message);
      });

      this.connection.on("VideoRequestEvent", (message: VideoRequestEvent) => {
        console.debug(`Received Video Request Event: ${JSON.stringify(message)}`);
        this.videoRequestEvent.fire(message);
      });

      this.connection.onreconnected(() => {
        if (this.group) {
          this.joinCouncil(this.group);
          if (userStore.canAccessVideoRequests()) {
            this.joinCouncilVideos(this.group);
          }
        }
      });

      this.start();
    }, 0);
  }

  private async start() {
    if (this.connection?.state === signalR.HubConnectionState.Disconnected) {
      this.startPromise = this.connection.start().catch(err => {
        console.log(err);
        setTimeout(() => this.start(), 5000);
      });
    }
    await this.startPromise;

    this.dispose = reaction(() => userStore.councilConfiguration?.name, this.onActiveCouncilChanged.bind(this), { fireImmediately: true });
  }

  private async onActiveCouncilChanged(activeCouncil: string | undefined) {
    await this.startPromise;
    if (this.group === activeCouncil) return;
    if (this.group !== undefined) {
      const group = this.group;
      this.connection
        ?.invoke("LeaveCouncilGroup", group)
        .then(function() {
          console.log(`Left group: ${group}`);
        })
        .catch(function(err) {
          return console.error("Error leaving group" + JSON.stringify(err));
        });
      this.connection
        ?.invoke("LeaveCouncilVideoGroup", group)
        .then(function() {
          console.log(`Left video group: ${group}`);
        })
        .catch(function(err) {
          return console.error("Error leaving video group" + JSON.stringify(err));
        });
    }
    if (activeCouncil) {
      this.joinCouncil(activeCouncil);
      if (userStore.canAccessVideoRequests()) {
        this.joinCouncilVideos(activeCouncil);
      }
    }
  }

  private joinCouncil(council: string) {
    this.group = council;
    this.connection
      ?.invoke("JoinCouncilGroup", council)
      .then(function() {
        console.log(`Joined group: ${council}`);
      })
      .catch(function(err) {
        return console.error("Error joining group" + JSON.stringify(err));
      });
  }

  private joinCouncilVideos(council: string) {
    this.connection
      ?.invoke("JoinCouncilVideoGroup", council)
      .then(function() {
        console.log(`Joined video group: ${council}`);
      })
      .catch(function(err) {
        return console.error("Error joining video group" + JSON.stringify(err));
      });
  }

  async stop() {
    if (this.dispose) this.dispose();
    if (this.group) this.group = undefined;
    if (this.connection && this.connection.state !== signalR.HubConnectionState.Disconnected && this.connection.state !== signalR.HubConnectionState.Disconnecting)
      await this.connection?.stop();
  }
}

const eventsHub = new EventsHub();

export default eventsHub;
